import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AlertTriangle, ChevronDown } from 'lucide-react'

import { dashboardViewActions } from '@/redux/slice/dashboardViewSlice'

interface RateLimitBannerProps {
  /**
   * Vendor display name, e.g. "CampMinder".
   * Interpolated into "Hold on — {vendor} is rate-limiting us."
   */
  vendor: string
  /**
   * Epoch ms when the rate-limit window clears.
   * Drives countdown: Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000))
   */
  rateLimitedUntil: number
}

/**
 * Rate-limit countdown banner — Slice C implementation.
 *
 * Renders when dashboardError.code === 'RATE_LIMIT' AND
 * Date.now() < rateLimitedUntil.
 *
 * Auto-dismisses (returns null) when countdown reaches 0.
 *
 * Design ref: designs/sync-error-handling/2026-05-05-rate-limit-banner-v1.html
 * Brand guide: §34.4 (RateLimitBanner anatomy + color tokens)
 * TDD ref: §6.2
 *
 * ARIA:
 *   - role="alert" aria-live="polite" (NOT aria-atomic — would re-announce
 *     the entire banner on every per-second countdown tick; spam.)
 *   - Countdown badge: role="timer" aria-label="Retry available in {N} seconds"
 *     role="timer" is a non-announcing role, so the per-second changes inside
 *     the badge do not trigger live-region announcements.
 *   - Disclosure button: aria-expanded + aria-controls
 *   - No dismiss button — banner self-resolves at countdown=0 (per DQ7).
 *
 * Banner stacking (CEO-locked 2026-05-05):
 *   RateLimitBanner is rendered ABOVE SyncFailedReconnectBanner when both
 *   RATE_LIMIT and AUTH_EXPIRED are simultaneously active. This component
 *   never suppresses the legacy auth banner — the parent (Canvas.tsx) renders
 *   both independently.
 */
export function RateLimitBanner({
  vendor,
  rateLimitedUntil,
}: RateLimitBannerProps) {
  const dispatch = useDispatch()
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000))
  )
  const [disclosureOpen, setDisclosureOpen] = useState(false)

  useEffect(() => {
    const tick = () => {
      setSecondsLeft(Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000)))
    }
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [rateLimitedUntil])

  // PR #335 review fix: when the countdown reaches 0, clear the rate-limit
  // slot in Redux so memoized selectors that key off rateLimitedUntil
  // (notably selectLastSyncSummary) recompute. Without this dispatch the
  // selector cache held 'rate_limited' forever because its inputs never
  // changed — only the wall-clock time did, which createSelector ignores.
  useEffect(() => {
    if (secondsLeft <= 0) {
      dispatch(dashboardViewActions.clearRateLimit())
    }
  }, [secondsLeft, dispatch])

  // Auto-dismiss when countdown clears
  if (secondsLeft <= 0) return null

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="rate-limit-banner"
      className="flex flex-col gap-2 px-5 py-3 bg-warning-foreground border-b border-warning/35 flex-shrink-0"
    >
      {/* Row 1: icon + headline + countdown badge */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* AlertTriangle — warning icon per brand guide §34.2 (RATE_LIMIT = warning/amber) */}
        {/* Mockup uses AlertTriangle shape for rate-limit. Brand guide §34.2 icons column
            lists Clock for RATE_LIMIT; mockup HTML uses AlertTriangle SVG path. Mockup
            wins as the visual spec — AlertTriangle is the designer's final intent. */}
        <AlertTriangle
          size={16}
          className="text-[hsl(34_100%_35%)] flex-shrink-0"
          aria-hidden="true"
          strokeWidth={2}
        />

        {/* Headline */}
        <span className="text-[13px] font-semibold text-[hsl(34_100%_30%)]">
          Hold on — {vendor} is rate-limiting us.
        </span>

        {/* Countdown badge — pill shape, monospace-numeric per design spec */}
        <span
          role="timer"
          aria-label={`Retry available in ${secondsLeft} seconds`}
          className="inline-flex items-center gap-1 bg-warning/15 border border-warning/40 rounded-full px-2.5 py-0.5 text-[12px] font-bold text-[hsl(34_100%_35%)] tabular-nums tracking-wide flex-shrink-0"
        >
          Retry in {secondsLeft}s
        </span>
      </div>

      {/* Row 2: body copy */}
      <p className="text-[12px] text-[hsl(34_100%_30%)] leading-relaxed pl-[26px]">
        We'll automatically unlock the Sync button when the wait clears.
        The countdown above updates every second.
      </p>

      {/* Row 3: disclosure toggle */}
      <div className="pl-[26px]">
        <button
          type="button"
          className="text-[11px] font-medium text-[hsl(34_100%_35%)] underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-1 rounded-sm"
          aria-expanded={disclosureOpen}
          aria-controls="rate-limit-explainer"
          onClick={() => setDisclosureOpen(v => !v)}
        >
          {disclosureOpen ? (
            <>
              Close{' '}
              <ChevronDown
                size={11}
                className="inline rotate-180 transition-transform"
                aria-hidden="true"
              />
            </>
          ) : (
            <>
              What's a rate limit?{' '}
              <ChevronDown
                size={11}
                className="inline transition-transform"
                aria-hidden="true"
              />
            </>
          )}
        </button>

        {disclosureOpen && (
          <div
            id="rate-limit-explainer"
            className="mt-1.5 px-3 py-2.5 bg-warning/8 border border-warning/20 rounded-md"
          >
            <p className="text-[11px] text-[hsl(34_100%_30%)] leading-relaxed">
              {vendor} limits how many data requests can be made per minute.
              When we hit that limit, their servers pause new requests for a short window.
              This is normal — it protects their system from overload. We'll retry
              automatically once the window clears.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
